import { ReactElement } from 'react';
import { ParserVars } from '../types/Run';

/**
 * Editor for `parser_vars.atmosphere.force_synthetic_dp` — replaces the
 * measured DynPres channels with the synthesized DynPresSynth channel during
 * reprocessing, even when probe dynamic pressure is present.
 *
 * Session scope is a plain on/off checkbox (off is stored as null so the
 * sub-object stays compact). Run scope is tri-state: inherit the session
 * value, force on, or force off (an explicit run-level `false` overrides a
 * session-level `true` via the run-over-session parser_vars merge).
 */

// Return a ParserVars blob with the flag set, preserving every other
// sub-object (catesby/traverse/imu/...) already stored at that scope.
export function withForceSyntheticDp(
  pv: ParserVars | null | undefined,
  next: boolean | null,
): ParserVars {
  const atmosphere = next == null
    ? null
    : { ...(pv?.atmosphere ?? {}), force_synthetic_dp: next };
  return { ...(pv ?? {}), version: pv?.version ?? 1, atmosphere };
}

interface ForceSyntheticDpControlProps {
  scope: 'session' | 'run';
  // The value stored at this scope (undefined/null → not set here)
  value: boolean | null | undefined;
  // Run scope only: the session-level value, shown as the inherit hint
  sessionValue?: boolean | null;
  saving: boolean;
  onSave: (next: boolean | null) => void;
}

export default function ForceSyntheticDpControl({
  scope,
  value,
  sessionValue,
  saving,
  onSave,
}: ForceSyntheticDpControlProps): ReactElement {
  const label = 'Force synthetic dynamic pressure';
  const hint = 'Reprocess uses the wheel-speed-derived dynamic pressure even when probe DP is present';

  if (scope === 'session') {
    return (
      <div className="cr-flex cr-items-center cr-gap-2">
        <input
          type="checkbox"
          id="force-synthetic-dp"
          checked={value === true}
          disabled={saving}
          onChange={(e) => onSave(e.target.checked ? true : null)}
          className="cr-h-4 cr-w-4 cr-accent-cirrusBlue-400 disabled:cr-opacity-60"
        />
        <label htmlFor="force-synthetic-dp" className="cr-text-sm cr-text-gray-700">
          {label}
          <span className="cr-ml-2 cr-text-xs cr-font-normal cr-text-gray-400">({hint})</span>
        </label>
      </div>
    );
  }

  const selectValue = value == null ? 'inherit' : value ? 'on' : 'off';
  const inheritedLabel = sessionValue === true ? 'on (session)' : 'off';

  return (
    <div className="cr-flex cr-items-center cr-gap-2" title={hint}>
      <span className="cr-text-sm cr-text-gray-700">{label}:</span>
      <select
        value={selectValue}
        disabled={saving}
        onChange={(e) => {
          const v = e.target.value;
          onSave(v === 'inherit' ? null : v === 'on');
        }}
        className="cr-rounded-md cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm cr-text-gray-700
          focus:cr-outline-2 focus:cr-outline-cirrusBlue-400 disabled:cr-opacity-60"
      >
        <option value="inherit">{`Inherit — ${inheritedLabel}`}</option>
        <option value="on">Force on</option>
        <option value="off">Force off</option>
      </select>
    </div>
  );
}
